/**
 * BillingRecord English/Spanish field accessors and save normalization.
 * DB columns and POST /api/billing-records still use Spanish keys; GET responses
 * are dual-key via billingRecordMapper. Forms must never write only one side.
 */
import type { BillingRecord } from '../../types';

/** Canonical EN/ES pairs for billing save bodies (Spanish DB columns + English mirrors). */
export const BILLING_BILINGUAL_PAIRS: [string, string][] = [
  ['invoice', 'factura'],
  ['thirdParty', 'tercero'],
  ['issueDate', 'emision'],
  ['currency', 'moneda'],
  ['country', 'pais'],
  ['costs', 'costos'],
  ['revenues', 'ingresos'],
  ['totalInvoiced', 'totalFacturado'],
  ['guidesValue', 'valorGuias'],
  ['difference', 'diferencia'],
  ['salesRep', 'comercial'],
];

export function billingInvoice(r: Partial<BillingRecord> | null | undefined): string {
  return r?.invoice ?? r?.factura ?? '';
}

export function billingThirdParty(r: Partial<BillingRecord> | null | undefined): string {
  return r?.thirdParty ?? r?.tercero ?? '';
}

export function billingIssueDate(r: Partial<BillingRecord> | null | undefined): string {
  return r?.issueDate ?? r?.emision ?? '';
}

export function billingCurrency(r: Partial<BillingRecord> | null | undefined): 'COP' | 'USD' {
  return (r?.currency ?? r?.moneda ?? 'COP') as 'COP' | 'USD';
}

export function billingCountry(r: Partial<BillingRecord> | null | undefined): BillingRecord['country'] {
  return (r?.country ?? r?.pais ?? 'Colombia') as BillingRecord['country'];
}

export function billingCosts(r: Partial<BillingRecord> | null | undefined): number {
  return Number(r?.costs ?? r?.costos) || 0;
}

export function billingRevenues(r: Partial<BillingRecord> | null | undefined): number {
  return Number(r?.revenues ?? r?.ingresos) || 0;
}

export function billingTotalInvoiced(r: Partial<BillingRecord> | null | undefined): number {
  return Number(r?.totalInvoiced ?? r?.totalFacturado) || 0;
}

export function billingGuidesValue(r: Partial<BillingRecord> | null | undefined): number {
  return Number(r?.guidesValue ?? r?.valorGuias) || 0;
}

export function billingDifference(r: Partial<BillingRecord> | null | undefined): number {
  return Number(r?.difference ?? r?.diferencia) || 0;
}

export function billingSalesRep(r: Partial<BillingRecord> | null | undefined): string {
  return r?.salesRep ?? r?.comercial ?? '';
}

/** When both EN/ES keys are present and differ, prefer the legacy Spanish field (forms/Excel). */
function pickBilingual<T>(en: T | undefined, es: T | undefined, fallback: T): T {
  if (en !== undefined && es !== undefined && en !== es) return es;
  return (en ?? es ?? fallback) as T;
}

/** Merge edits keeping English and legacy Spanish keys in sync. */
export function patchBillingRecord(
  record: BillingRecord,
  patch: Partial<BillingRecord>,
): BillingRecord {
  const next: BillingRecord = { ...record, ...patch };

  if (patch.invoice !== undefined || patch.factura !== undefined) {
    const v = pickBilingual(patch.invoice, patch.factura, '');
    next.invoice = v;
    next.factura = v;
  }
  if (patch.thirdParty !== undefined || patch.tercero !== undefined) {
    const v = pickBilingual(patch.thirdParty, patch.tercero, '');
    next.thirdParty = v;
    next.tercero = v;
  }
  if (patch.issueDate !== undefined || patch.emision !== undefined) {
    const v = pickBilingual(patch.issueDate, patch.emision, '');
    next.issueDate = v;
    next.emision = v;
  }
  if (patch.currency !== undefined || patch.moneda !== undefined) {
    const v = pickBilingual(patch.currency, patch.moneda, 'COP') as 'COP' | 'USD';
    next.currency = v;
    next.moneda = v;
  }
  if (patch.country !== undefined || patch.pais !== undefined) {
    const v = pickBilingual(patch.country, patch.pais, 'Colombia') as BillingRecord['country'];
    next.country = v;
    next.pais = v;
  }
  if (patch.costs !== undefined || patch.costos !== undefined) {
    const v = Number(pickBilingual(patch.costs, patch.costos, 0)) || 0;
    next.costs = v;
    next.costos = v;
  }
  if (patch.revenues !== undefined || patch.ingresos !== undefined) {
    const v = Number(pickBilingual(patch.revenues, patch.ingresos, 0)) || 0;
    next.revenues = v;
    next.ingresos = v;
  }
  if (patch.totalInvoiced !== undefined || patch.totalFacturado !== undefined) {
    const v = Number(pickBilingual(patch.totalInvoiced, patch.totalFacturado, 0)) || 0;
    next.totalInvoiced = v;
    next.totalFacturado = v;
  }
  if (patch.guidesValue !== undefined || patch.valorGuias !== undefined) {
    const v = Number(pickBilingual(patch.guidesValue, patch.valorGuias, 0)) || 0;
    next.guidesValue = v;
    next.valorGuias = v;
  }
  if (patch.difference !== undefined || patch.diferencia !== undefined) {
    const v = Number(pickBilingual(patch.difference, patch.diferencia, 0)) || 0;
    next.difference = v;
    next.diferencia = v;
  }
  if (patch.salesRep !== undefined || patch.comercial !== undefined) {
    const v = pickBilingual(patch.salesRep, patch.comercial, '');
    next.salesRep = v;
    next.comercial = v;
  }

  return next;
}

/** Body for POST /api/billing-records — Spanish DB columns + English mirrors. */
export function toBillingRecordSaveBody(record: Partial<BillingRecord>): Record<string, unknown> {
  const synced = patchBillingRecord({ ...record } as BillingRecord, record);
  const invoice = billingInvoice(synced);
  const thirdParty = billingThirdParty(synced);
  const issueDate = billingIssueDate(synced);
  const currency = billingCurrency(synced);
  const country = billingCountry(synced);
  const costs = billingCosts(synced);
  const revenues = billingRevenues(synced);
  const totalInvoiced = billingTotalInvoiced(synced);
  const guidesValue = billingGuidesValue(synced);
  const difference = billingDifference(synced);
  const salesRep = billingSalesRep(synced);

  return {
    ...synced,
    id: synced.id,
    factura: invoice,
    invoice,
    tercero: thirdParty,
    thirdParty,
    nit: record.nit ?? '',
    emision: issueDate,
    issueDate,
    moneda: currency,
    currency,
    pais: country,
    country,
    costos: costs,
    costs,
    ingresos: revenues,
    revenues,
    totalFacturado: totalInvoiced,
    totalInvoiced,
    valorGuias: guidesValue,
    guidesValue,
    diferencia: difference,
    difference,
    comercial: salesRep,
    salesRep,
  };
}

/** Server-side: accept English or Spanish keys from any client. */
export function normalizeBillingRecordBody(body: Record<string, unknown>): Record<string, unknown> {
  const r = { ...body } as Partial<BillingRecord>;
  return toBillingRecordSaveBody(r);
}
